import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import './RegistrationPage.css';
import api from '../api';

export default function RegistrationPage() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const modalCloseBtnRef = useRef(null);
  const [registration, setRegistration] = useState({
    name: '',
    email: '',
    mobile: '',
    adults: 1,
    kids: 0,
    volunteering: false
  });
  const [registrationMsg, setRegistrationMsg] = useState({ type: '', text: '' });
  const [cancelMsg, setCancelMsg] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const isEventInPast = () => {
    if (!event) return false;
    const eventTime = event.end_time || event.date_time;
    if (!eventTime) return false;
    return new Date(eventTime) < new Date();
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleImageError = (e) => {
    e.target.style.display = 'none';
    e.target.parentElement.style.background = '#e9ecef';
    e.target.parentElement.style.borderRadius = '4px';
  };

  // Focus trap and ESC key handling for image modal
  useEffect(() => {
    if (!isImageModalOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsImageModalOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    modalCloseBtnRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isImageModalOpen]);

  useEffect(() => {
    if (eventId) {
      loadEvent();
    }
  }, [eventId]);

  const loadEvent = async () => {
    setLoading(true);
    setLoadError('');
    setEvent(null);
    try {
      // Try to get the specific event by ID (public endpoint)
      const eventRes = await api.get('/api/events/' + eventId);
      setEvent(eventRes.data);
    } catch (err) {
      setLoadError(err.response?.data?.detail || 'Event not found');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setRegistration(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : 
              type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!registration.name.trim() || !registration.email.trim()) {
      setRegistrationMsg({ type: 'error', text: 'Name and email are required' });
      return;
    }
    if (!isValidEmail(registration.email.trim())) {
      setRegistrationMsg({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }
    
    setSubmitting(true);
    setRegistrationMsg({ type: '', text: '' });
    
    try {
      const params = new URLSearchParams();
      params.append('name', registration.name);
      params.append('email', registration.email);
      params.append('mobile', registration.mobile || '');
      params.append('adults', String(registration.adults));
      params.append('kids', String(registration.kids));
      params.append('volunteering', String(registration.volunteering));
      
      await api.post('/api/events/' + eventId + '/register', params);
      
      setRegistrationMsg({ 
        type: 'success', 
        text: event.is_paid ? 
          'Registration submitted! Awaiting approval.' : 
          'Registration confirmed!'
      });
      
      // Reset form but keep values for potential resubmission
      setRegistration(prev => ({ 
        ...prev, 
        adults: 1, 
        kids: 0 
      }));
    } catch (err) {
      let errorMsg = 'Registration failed';
      if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      } else if (err.message) {
        errorMsg = err.message;
      }
      setRegistrationMsg({ type: 'error', text: errorMsg });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelSubmit = async (e) => {
    e.preventDefault();
    if (!registration.email.trim()) {
      setCancelMsg({ type: 'error', text: 'Email is required to cancel registration' });
      return;
    }
    if (!isValidEmail(registration.email.trim())) {
      setCancelMsg({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }
    
    setSubmitting(true);
    setCancelMsg({ type: '', text: '' });
    
    try {
      const params = new URLSearchParams();
      params.append('email', registration.email);
      
      await api.post('/api/events/' + eventId + '/cancel', params);
      
      setCancelMsg({ 
        type: 'success', 
        text: 'Registration cancelled successfully' 
      });
      setRegistration(prev => ({ 
        ...prev, 
        email: '' 
      }));
    } catch (err) {
      let errorMsg = 'Cancellation failed';
      if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      } else if (err.message) {
        errorMsg = err.message;
      }
      setCancelMsg({ type: 'error', text: errorMsg });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="registration-page">
        <div className="registration-panel">Loading event details...</div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="registration-page">
        <div className="registration-panel" role="alert">
          {loadError || 'Event not found'}
        </div>
      </div>
    );
  }

  // Format start time: "Saturday Jun 4, 2026 at 4:00 PM"
  const formatStartDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return '';
    const date = new Date(dateTimeStr);
    const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.toLocaleDateString('en-US', { day: 'numeric' });
    const year = date.toLocaleDateString('en-US', { year: 'numeric' });
    const datePart = `${weekday} ${month} ${day}, ${year}`;
    const timePart = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    return `${datePart} at ${timePart}`;
  };
  
  // Format end time: "7:00 PM"
  const formatEndTime = (endTimeStr) => {
    if (!endTimeStr) return '';
    const date = new Date(endTimeStr);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  
  const startTimeStr = formatStartDateTime(event.date_time);
  const endTimeStr = formatEndTime(event.end_time);

  return (
    <div className="registration-page">
      <div className="registration-panel">
        <h2>{event.event_name}</h2>
        
        {event.is_cancelled ? (
          <div className="event-details" style={{ background: '#f8d7da', border: '1px solid #f5c6cb', marginBottom: '1rem' }}>
            <p style={{ color: '#721c24', fontWeight: 'bold', marginBottom: '0.5rem' }}>
              <i className="fa-solid fa-exclamation-triangle" style={{ marginRight: '0.5rem' }}></i>
              This Event Has Been Cancelled
            </p>
            {event.cancelled_reason && (
              <p style={{ color: '#721c24', fontStyle: 'italic' }}>
                Reason: {event.cancelled_reason}
              </p>
            )}
            <p style={{ color: '#721c24', marginTop: '0.5rem' }}>
              Registration is no longer available for this event.
            </p>
          </div>
        ) : (
          <>
            <div className="event-details">
              <p className="event-time-info">
                <strong>Start Time:</strong> {startTimeStr}
                {endTimeStr && (
                  <span className="end-time-separator">
                    {' - '}
                    <strong>End Time:</strong> {endTimeStr}
                  </span>
                )}
              </p>
              {endTimeStr && (
                <p className="event-end-time-mobile">
                  <strong>End Time:</strong> {endTimeStr}
                </p>
              )}
              <p className="event-location"><strong>Location:</strong> {event.address}</p>
              <p className="event-attendees">
                Total Attendees: {event.adults_count || 0} adults, {event.kids_count || 0} kids
              </p>
            </div>
            {event.image_url && (
              <div className="event-image-wrapper">
                <img 
                  src={event.image_url} 
                  alt={event.event_name} 
                  className="event-image"
                  onClick={() => setIsImageModalOpen(true)}
                  onError={handleImageError}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            )}
          </>
        )}
        
        {!event.is_cancelled && (
          <>
            {isEventInPast() && (
              <div className="registration-closed-message">
                <div className="msg msg-closed">
                  Registration for this event is now closed. Thank you for your interest!
                </div>
              </div>
            )}
            <form onSubmit={handleSubmit} className="registration-form">
          <h3>Registration Form</h3>
          
          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              name="name"
              value={registration.name}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              name="email"
              value={registration.email}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label>Mobile (Optional)</label>
            <input
              type="tel"
              name="mobile"
              value={registration.mobile}
              onChange={handleChange}
            />
          </div>
          
          <div className="form-group">
            <label>Number of Adults</label>
            <input
              type="number"
              name="adults"
              value={registration.adults}
              onChange={handleChange}
              min="1"
            />
          </div>
          
          <div className="form-group">
            <label>Number of Kids</label>
            <input
              type="number"
              name="kids"
              value={registration.kids}
              onChange={handleChange}
              min="0"
            />
          </div>
          
          <div className="form-group">
            <label className="checkbox-label volunteering-highlight">
              <input
                type="checkbox"
                name="volunteering"
                checked={registration.volunteering}
                onChange={handleChange}
              />
              Volunteering - I would like to volunteer (Seva) for this event
            </label>
          </div>
          
          {event.is_paid && (
            <div className="payment-info">
              <h3>Payment Information</h3>
              <p>This is a paid event with a minimum contribution of ${event.minimum_amount || 0}.</p>
              <div className="payment-options">
                <p>You may pay via:</p>
                <div className="payment-method">
                  <strong>Zelle:</strong> Send to vedantasoclt@gmail.com
                </div>
                <div className="payment-method">
                  <strong>PayPal: </strong> 
                  <a href="https://paypal.com/us/fundraiser/charity/5165656" 
                     target="_blank" rel="noopener noreferrer">
                    Donate via PayPal
                  </a>
                </div>
                <div className="payment-qr-codes" style={{ display: 'flex', gap: '40px', flexWrap: 'wrap', marginTop: '20px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <img src="/images/zelle-qr-vcc.jpeg" alt="Zelle QR Code" style={{ width: '200px', height: '200px', border: '1px solid #ddd', padding: '5px' }} />
                    <p style={{ marginTop: '10px', fontSize: '14px', color: '#333' }}>Zelle: vedantasoclt@gmail.com</p>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <a href="https://paypal.com/us/fundraiser/charity/5165656" target="_blank" rel="noopener noreferrer">
                      <img src="/images/paypal-gofund-vcc.png" alt="PayPal QR Code" style={{ width: '200px', height: '200px', border: '1px solid #ddd', padding: '5px' }} />
                    </a>
                    <p style={{ marginTop: '10px', fontSize: '14px', color: '#333' }}>PayPal</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {!event.is_paid && event.include_donation_options && (
            <div className="donation-info">
              <h3>Donation Information</h3>
              <p>Donation is Optional but we are thankful for your contribution!</p>
              <div className="payment-options" style={{ borderTop: "1px solid #eee", paddingTop: "1rem", marginTop: "1rem" }}>
                <div className="payment-method">
                  <strong>Zelle:</strong> Send to vedantasoclt@gmail.com
                </div>
                <div className="payment-method">
                  <strong>PayPal: </strong> 
                  <a href="https://paypal.com/us/fundraiser/charity/5165656" target="_blank" rel="noopener noreferrer">
                    Donate via PayPal
                  </a>
                </div>
                <div className="payment-qr-codes" style={{ display: "flex", gap: "40px", flexWrap: "wrap", marginTop: "20px" }}>
                  <div style={{ textAlign: "center" }}>
                    <img src="/images/zelle-qr-vcc.jpeg" alt="Zelle QR Code" style={{ width: "200px", height: "200px", border: "1px solid #ddd", padding: "5px" }} />
                    <p style={{ marginTop: "10px", fontSize: "14px", color: "#333" }}>Zelle: vedantasoclt@gmail.com</p>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <a href="https://paypal.com/us/fundraiser/charity/5165656" target="_blank" rel="noopener noreferrer">
                      <img src="/images/paypal-gofund-vcc.png" alt="PayPal QR Code" style={{ width: "200px", height: "200px", border: "1px solid #ddd", padding: "5px" }} />
                    </a>
                    <p style={{ marginTop: "10px", fontSize: "14px", color: "#333" }}>PayPal</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <button type="submit" disabled={submitting || isEventInPast()}>
            {submitting ? 'Submitting...' : 'Register'}
          </button>
        </form>
        {registrationMsg.text && (
          <div className={`msg msg-${registrationMsg.type}`}>
            {registrationMsg.text}
          </div>
        )}
          </>
        )}
        
        {!event.is_cancelled && (
          <div className="cancel-section">
            <h3>Cancel Registration</h3>
            <p>Enter your email address to cancel your registration:</p>
            <form onSubmit={handleCancelSubmit} className="cancel-form">
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={registration.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <button type="submit" disabled={submitting || isEventInPast()}>
                {submitting ? 'Cancelling...' : 'Cancel Registration'}
              </button>
            </form>
            {cancelMsg.text && (
              <div className={`msg msg-${cancelMsg.type}`}>
                {cancelMsg.text}
              </div>
            )}
          </div>
        )}
      </div>

      {isImageModalOpen && event.image_url && (
        <div
          className="image-modal-overlay"
          onClick={() => setIsImageModalOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={`Full view of ${event.event_name} image`}
        >
          <button
            type="button"
            className="image-modal-close"
            onClick={() => setIsImageModalOpen(false)}
            aria-label="Close image"
            autoFocus
            ref={modalCloseBtnRef}
          >
            &times;
          </button>
          <div
            className="image-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={event.image_url}
              alt={event.event_name}
              className="image-modal-img"
            />
          </div>
        </div>
      )}
    </div>
  );
}
